import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ConfigProvider, Input, Switch } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import { useOnboarding } from "../../../../contexts/OnboardingContext";
import { useCategoriesContext } from "../../../../contexts/CategoriesContext";
import { useToast } from "../../../../contexts/ToastContext";
import useCarPricingMode from "../../../cars/hooks/useCarPricingMode";
import CategoryFormModal from "../../../cars/components/category/CategoryFormModal";

const INITIAL_VISIBLE = 10;
const LOAD_MORE_COUNT = 8;

const PRESET_CATEGORIES = [
    { key: "mpv", name: "MPV (Multi-Purpose Vehicle)" },
    { key: "van", name: "Cargo/Passenger Van" },
    { key: "muscle", name: "Muscle Car" },
    { key: "offroader", name: "Off-Roader" },
    { key: "pickup", name: "Pickup Truck" },
    { key: "ev", name: "Electric Vehicle (EV)" },
    { key: "wagon", name: "Station Wagon (Estate)" },
    { key: "supercar", name: "Supercar" },
    { key: "sports", name: "Sports Car" },
    { key: "hypercar", name: "Hypercar" },
    { key: "suv", name: "SUV" },
    { key: "sedan", name: "Sedan" },
    { key: "luxury", name: "Luxury" },
    { key: "convertible", name: "Convertible" },
    { key: "coupe", name: "Coupe" },
    { key: "hatchback", name: "Hatchback" },
    { key: "minivan", name: "Minivan" },
    { key: "crossover", name: "Crossover" },
];

const normalizeName = (name = "") => name.trim().toLowerCase();

const buildQuickPayload = (name, isCarPricingMode) => {
    if (isCarPricingMode) {
        return { categoryName: name, status: "active" };
    }
    return {
        categoryName: name,
        status: "active",
        seats: 5,
        bags: 3,
        hourlyRate: { disabled: true, rate: 0 },
        dailyRate: { disabled: true, rate: 0 },
        weeklyRate: { disabled: true, rate: 0 },
        monthlyRate: { disabled: true, rate: 0 },
    };
};

const Categories = forwardRef((props, ref) => {
    const { t } = useTranslation("onboarding");
    const { updateStepProgress, STEP_KEY_TO_INDEX } = useOnboarding();
    const { categories, fetchCategories, addCategory } = useCategoriesContext();
    const toast = useToast();
    const isCarPricingMode = useCarPricingMode();

    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
    const [selectedNames, setSelectedNames] = useState(() => new Set());
    const [saving, setSaving] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);

    const loadCategories = useCallback(async () => {
        const { data } = await fetchCategories({}, { page: 1, limit: 200 });
        return data || [];
    }, [fetchCategories]);

    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            const data = await loadCategories();
            if (cancelled) return;
            setSelectedNames(new Set(data.map((item) => item.categoryName).filter(Boolean)));
        };
        init();
        return () => {
            cancelled = true;
        };
    }, [loadCategories]);

    const catalog = useMemo(() => {
        const existingExtras = (categories || [])
            .filter((item) => item?.categoryName)
            .filter((item) => !PRESET_CATEGORIES.some((preset) => normalizeName(preset.name) === normalizeName(item.categoryName)))
            .map((item) => ({ key: item.categoryId || item.categoryName, name: item.categoryName }));

        return [...PRESET_CATEGORIES, ...existingExtras];
    }, [categories]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return catalog;
        return catalog.filter((item) => item.name.toLowerCase().includes(term));
    }, [catalog, search]);

    const visibleItems = filtered.slice(0, visibleCount);
    const leftColumn = visibleItems.slice(0, Math.ceil(visibleItems.length / 2));
    const rightColumn = visibleItems.slice(Math.ceil(visibleItems.length / 2));
    const hasMore = visibleCount < filtered.length;

    const toggleCategory = (name, checked) => {
        setSelectedNames((prev) => {
            const next = new Set(prev);
            if (checked) next.add(name);
            else next.delete(name);
            return next;
        });
    };

    const submit = async () => {
        if (selectedNames.size === 0) {
            toast.error({ message: t("steps.categories.atLeastOne") });
            return false;
        }

        setSaving(true);
        try {
            const existing = (await loadCategories()) || [];
            const existingNames = new Set(existing.map((item) => normalizeName(item.categoryName)));
            const toCreate = [...selectedNames].filter((name) => !existingNames.has(normalizeName(name)));

            await Promise.all(
                toCreate.map(async (name) => {
                    const response = await apiClient.post(
                        "/api/car-category-v2/create",
                        buildQuickPayload(name, isCarPricingMode),
                    );
                    if (response?.data?.success && response?.data?.data) {
                        addCategory(response.data.data);
                    }
                }),
            );

            await updateStepProgress("categories", STEP_KEY_TO_INDEX.categories ?? 2, true);
            return true;
        } catch {
            toast.error({ message: t("messages.saveError") });
            return false;
        } finally {
            setSaving(false);
        }
    };

    useImperativeHandle(ref, () => ({ submit }));

    const renderColumn = (items) => (
        <ul className="flex flex-col gap-4">
            {items.map((item) => (
                <li key={item.key} className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-800">{item.name}</span>
                    <Switch
                        checked={selectedNames.has(item.name)}
                        onChange={(checked) => toggleCategory(item.name, checked)}
                    />
                </li>
            ))}
        </ul>
    );

    return (
        <ConfigProvider theme={{ token: { colorPrimary: "#5d5dff" } }}>
            <div>
                <h1 className="text-[28px] font-bold tracking-tight text-gray-900 sm:text-[32px]">
                    {t("steps.categories.heading")}
                </h1>
                <p className="mt-1 mb-8 text-sm leading-relaxed text-gray-500 sm:text-base">
                    {t("steps.categories.selectDescription")}
                </p>

                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Input
                        size="large"
                        allowClear
                        prefix={<SearchOutlined className="text-gray-400" />}
                        placeholder={t("steps.categories.searchCategories")}
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setVisibleCount(INITIAL_VISIBLE);
                        }}
                        className="rounded-lg sm:flex-1"
                    />
                    <button
                        type="button"
                        onClick={() => setAddModalOpen(true)}
                        className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg border border-[#5d5dff] px-4 text-sm font-medium text-[#5d5dff] hover:bg-[#5d5dff]/5">
                        <PlusOutlined />
                        {t("steps.categories.addCategory")}
                    </button>
                </div>

                {visibleItems.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-500">{t("steps.categories.noCategories")}</p>
                ) : (
                    <div className="grid grid-cols-1 gap-x-12 gap-y-4 md:grid-cols-2">
                        {renderColumn(leftColumn)}
                        {renderColumn(rightColumn)}
                    </div>
                )}

                {hasMore ? (
                    <div className="mt-8 flex justify-center">
                        <button
                            type="button"
                            onClick={() => setVisibleCount((count) => count + LOAD_MORE_COUNT)}
                            className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900">
                            {t("steps.categories.loadMore")}
                            <ChevronDown size={16} />
                        </button>
                    </div>
                ) : null}

                {saving ? <p className="mt-4 text-sm text-gray-500">{t("common.saving")}</p> : null}

                <CategoryFormModal
                    open={addModalOpen}
                    onCancel={() => setAddModalOpen(false)}
                    onSuccess={async () => {
                        const data = await loadCategories();
                        setSelectedNames(new Set(data.map((item) => item.categoryName).filter(Boolean)));
                    }}
                />
            </div>
        </ConfigProvider>
    );
});

Categories.displayName = "Categories";

export default Categories;
